<template>
  <div v-if="isMedAdherence" class="py-1 space-y-1">
    <!-- Priority Tier Badge -->
    <div v-if="priorityTier" class="mb-1">
      <span class="px-2 py-0.5 rounded text-xs font-bold" :class="getPriorityBadgeClass(priorityTier)">
        P{{ displayPriorityNumber(priorityTier) }}
      </span>
    </div>

    <!-- MedAdherence Display: Display each medication's last impactable date -->
    <div v-for="med in medications" :key="med.type" v-show="med.date">
      <div class="flex items-center gap-1 relative"
           :class="{ 'bg-blue-50 rounded px-1 -mx-1': med.isNext }">

        <!-- Next indicator arrow -->
        <span v-if="med.isNext" class="absolute -left-3 text-blue-600 font-bold animate-pulse">
          →
        </span>

        <!-- Icon based on urgency -->
        <span :class="getIconClass(med.daysRemaining)">
          {{ getIcon(med.daysRemaining) }}
        </span>

        <!-- Medication type badge with special styling if it's next -->
        <span class="text-xs font-semibold"
              :class="med.isNext ? 'text-blue-700 bg-blue-100 px-1 rounded' : 'text-gray-600'">
          {{ med.type.toUpperCase() }}
        </span>

        <!-- Date with color coding -->
        <span :class="getDateClass(med.daysRemaining)" class="text-xs">
          {{ formatDate(med.date) }}
        </span>

        <!-- Days remaining badge if critical -->
        <span v-if="med.daysRemaining <= 7 && med.daysRemaining >= 0"
              class="px-1 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">
          {{ med.daysRemaining }}d
        </span>

        <!-- Star indicator if this is the next one -->
        <span v-if="med.isNext" class="text-yellow-500 text-xs ml-auto">
          ⭐
        </span>
      </div>
    </div>

    <!-- Show "No priority data" if no medications have dates -->
    <div v-if="medications.every(m => !m.date)" class="text-xs text-gray-400 italic">
      No priority data
    </div>
  </div>

  <div v-else class="py-1">
    <!-- Generic Priority Score Display -->
    <div class="text-sm font-semibold text-gray-700">
      {{ displayPriorityScore }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'CC_Priority_Column_Generic',
  props: {
    value: {
      required: false
    },
  },
  data() {
    return {
      medicationTypes: ['dbts', 'ace', 'statn']
    }
  },
  computed: {
    isMedAdherence() {
      return this.params.metricId === 45;
    },

    priorityTier() {
      // Get priority_tier from backend (1-5, where 5 is highest urgency)
      // Display is inverted: tier 5 = P1, tier 4 = P2, etc.
      return this.params.data?.priority_tier || null;
    },

    displayPriorityScore() {
      // Use top-level priority_score (aggregated from opportunities by serializer)
      const score = this.params.data?.priority_score;
      if (score === null || score === undefined) {
        return 'N/A';
      }
      return parseFloat(score).toFixed(2);
    },

    taskValidThru() {
      return this.params.data?.valid_thru;
    },

    medications() {
      if (!this.isMedAdherence || !this.params.data?.metadata?.priority_reasons) {
        return [];
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Find the earliest date to identify which medication is "next"
      let earliestDate = null;
      let earliestMedType = null;

      const meds = this.medicationTypes.map(medType => {
        const lastImpactableDateField = `${medType}_lastdaytofail`;
        const lastImpactableDate = this.params.data.metadata.priority_reasons[lastImpactableDateField];

        if (lastImpactableDate) {
          const impactableDate = new Date(lastImpactableDate);
          impactableDate.setHours(0, 0, 0, 0);
          const daysRemaining = Math.ceil((impactableDate - today) / (1000 * 60 * 60 * 24));

          // Track the earliest date that's still in the future
          if (daysRemaining >= 0 && (!earliestDate || impactableDate < earliestDate)) {
            earliestDate = impactableDate;
            earliestMedType = medType;
          }

          return {
            type: medType,
            date: lastImpactableDate,
            daysRemaining: daysRemaining,
            isNext: false // Will be set below
          };
        }

        return {
          type: medType,
          date: null,
          daysRemaining: null,
          isNext: false
        };
      });

      // Mark the medication that's next in line
      if (earliestMedType) {
        const nextMed = meds.find(m => m.type === earliestMedType);
        if (nextMed) {
          nextMed.isNext = true;
        }
      }

      return meds.filter(med => med.date); // Only return medications with dates
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const [datePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    },

    getIcon(daysRemaining) {
      if (daysRemaining === null) return '';
      if (daysRemaining < 0) return '❌'; // Past due
      if (daysRemaining <= 7) return '🚨'; // Critical
      if (daysRemaining <= 30) return '⚠️'; // Urgent
      if (daysRemaining <= 60) return '📅'; // Upcoming
      return '📅'; // Normal
    },

    getIconClass(daysRemaining) {
      if (daysRemaining === null) return '';
      if (daysRemaining < 0) return 'text-gray-500'; // Past due
      if (daysRemaining <= 7) return 'text-red-600'; // Critical
      if (daysRemaining <= 30) return 'text-orange-500'; // Urgent
      if (daysRemaining <= 60) return 'text-yellow-500'; // Upcoming
      return 'text-blue-500'; // Normal
    },

    getDateClass(daysRemaining) {
      if (daysRemaining === null) return 'text-gray-600';
      if (daysRemaining < 0) return 'text-gray-500 line-through'; // Past due
      if (daysRemaining <= 7) return 'text-red-700 font-semibold'; // Critical
      if (daysRemaining <= 30) return 'text-orange-600 font-semibold'; // Urgent
      if (daysRemaining <= 60) return 'text-yellow-700'; // Upcoming
      return 'text-gray-700'; // Normal
    },

    displayPriorityNumber(tier) {
      // Invert the display: tier 5 shows as P1, tier 4 shows as P2, etc.
      return 6 - tier;
    },

    getPriorityBadgeClass(tier) {
      // Priority 5 = P1 = highest (red), Priority 1 = P5 = lowest (gray)
      const classes = {
        5: 'bg-red-600 text-white',        // P1 - Critical
        4: 'bg-primary-500 text-white',     // P2 - Urgent
        3: 'bg-yellow-500 text-black',     // P3 - High
        2: 'bg-primary-500 text-white',       // P4 - Medium
        1: 'bg-gray-400 text-white'        // P5 - Routine
      };
      return classes[tier] || 'bg-gray-300 text-gray-700';
    }
  }
}
</script>

<style scoped>
/* Ensure the cell doesn't overflow */
.space-y-1 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 0.25rem;
}

/* Animation for the pulsing arrow */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
